import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck, Trash2, Settings, Inbox, BellOff, BellRing } from 'lucide-react';
import { PageLayout } from '../components/layout/PageLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useNotifications } from '../context/NotificationContext';
import { api, formatDateTime } from '../lib/api';
import { toast } from 'sonner';

const NotificationsPage = () => {
  const navigate = useNavigate();
  const { unreadCount, markAsRead, markAllAsRead, deleteOne, clearAll, refresh, requestBrowserPermission } = useNotifications();
  const [tab, setTab] = useState('all');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [browserPerm, setBrowserPerm] = useState(typeof window !== 'undefined' && 'Notification' in window ? Notification.permission : 'unsupported');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/notifications', {
        params: { limit: 100, unread_only: tab === 'unread' },
      });
      setItems(res.data?.items || []);
    } catch (e) {
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const handleClick = async (n) => {
    if (!n.read) {
      await markAsRead(n.id);
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
    }
    if (n.link) navigate(n.link);
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    await deleteOne(id);
    setItems((prev) => prev.filter((n) => n.id !== id));
  };

  const handleMarkAllRead = async () => {
    await markAllAsRead();
    await load();
    toast.success('All notifications marked as read');
  };

  const handleClearAll = async () => {
    if (!window.confirm('Delete all notifications? This cannot be undone.')) return;
    await clearAll();
    setItems([]);
    toast.success('All notifications cleared');
  };

  const handleEnableBrowser = async () => {
    const result = await requestBrowserPermission();
    setBrowserPerm(result);
    if (result === 'granted') toast.success('Browser notifications enabled');
    else if (result === 'denied') toast.error('Browser notifications are blocked. Enable them in your browser settings.');
    else if (result === 'unsupported') toast.error('Your browser does not support notifications');
  };

  return (
    <PageLayout>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight flex items-center gap-3">
              <Bell className="w-8 h-8 text-primary" />
              Notifications
            </h1>
            <p className="text-muted-foreground mt-2 text-sm">
              {unreadCount > 0 ? `You have ${unreadCount} unread notification${unreadCount === 1 ? '' : 's'}.` : 'You\'re all caught up.'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="gap-2"
              onClick={() => navigate('/notifications/preferences')}
              data-testid="notification-preferences-link"
            >
              <Settings className="w-4 h-4" />
              Preferences
            </Button>
          </div>
        </div>

        {browserPerm !== 'granted' && browserPerm !== 'unsupported' && (
          <Card className="mb-6 border-primary/30 bg-primary/5">
            <CardContent className="py-4 px-5 flex items-center gap-3 flex-wrap">
              <BellRing className="w-5 h-5 text-primary flex-shrink-0" />
              <div className="flex-1 min-w-[200px]">
                <p className="text-sm font-medium">Enable browser notifications</p>
                <p className="text-xs text-muted-foreground">Get instant alerts even when this tab isn't focused.</p>
              </div>
              <Button size="sm" onClick={handleEnableBrowser} data-testid="enable-browser-notifications-btn">
                Enable
              </Button>
            </CardContent>
          </Card>
        )}

        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList>
              <TabsTrigger value="all" data-testid="notif-tab-all">All</TabsTrigger>
              <TabsTrigger value="unread" data-testid="notif-tab-unread">
                Unread {unreadCount > 0 ? `(${unreadCount})` : ''}
              </TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <Button variant="outline" size="sm" className="gap-2" onClick={handleMarkAllRead} data-testid="mark-all-read-page-btn">
                <CheckCheck className="w-4 h-4" />
                Mark all read
              </Button>
            )}
            {items.length > 0 && (
              <Button variant="ghost" size="sm" className="gap-2 text-destructive" onClick={handleClearAll} data-testid="clear-all-notif-btn">
                <Trash2 className="w-4 h-4" />
                Clear all
              </Button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="py-20 flex justify-center">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center flex flex-col items-center gap-3">
              <Inbox className="w-12 h-12 text-muted-foreground/50" />
              <p className="text-muted-foreground">
                {tab === 'unread' ? 'No unread notifications' : 'No notifications yet'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <ul className="space-y-2" data-testid="notifications-list">
            {items.map((n) => (
              <li
                key={n.id}
                data-testid={`notification-row-${n.id}`}
                onClick={() => handleClick(n)}
                className={`p-4 rounded-xl border cursor-pointer transition-all hover:shadow-md ${
                  !n.read
                    ? 'bg-primary/5 border-primary/30'
                    : 'bg-card border-border/50'
                }`}
              >
                <div className="flex items-start gap-3">
                  {!n.read && <span className="w-2 h-2 rounded-full bg-primary mt-2 flex-shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3">
                      <p className={`text-sm leading-snug ${!n.read ? 'font-semibold' : 'font-medium'}`}>
                        {n.title}
                      </p>
                      <button
                        className="text-muted-foreground hover:text-destructive transition-colors flex-shrink-0"
                        onClick={(e) => handleDelete(e, n.id)}
                        aria-label="Delete notification"
                        data-testid={`delete-notification-${n.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">{n.body}</p>
                    <p className="text-xs text-muted-foreground/70 mt-2">
                      {formatDateTime(n.created_at)}
                    </p>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </PageLayout>
  );
};

export default NotificationsPage;
